class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    
    // Store tab state
    this.currentTab = 'tab1'; // Default to cart tab
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        
        // Initialize tabs after drawer opens
        this.initializeTabs();
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    
    // Store current tab state before re-rendering
    const activeTab = this.querySelector('.tab.active');
    if (activeTab) {
      this.currentTab = activeTab.getAttribute('data-tab');
    }
    
    // Store the current wishlist count before re-render
    const wishlistTab = this.querySelector('.cart-wishlist');
    if (wishlistTab) {
      const wishlistText = wishlistTab.textContent;
      const match = wishlistText.match(/\((\d+)\)/);
      if (match) {
        this.wishlistCount = match[1];
      }
    }
    
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const newHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      
      // Preserve tab-related attributes when updating the drawer header
      if (section.id === 'cart-drawer' && newHTML.includes('drawer__header')) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = newHTML;
        
        // Find the drawer header in the new HTML
        const newHeader = tempDiv.querySelector('.drawer__header');
        if (newHeader) {
          // Apply tab classes to the header elements
          const cartTitle = newHeader.querySelector('.cart-title');
          const wishlistTitle = newHeader.querySelector('.cart-wishlist');
          
          if (cartTitle && !cartTitle.classList.contains('tab')) {
            cartTitle.classList.add('tab');
            cartTitle.setAttribute('data-tab', 'tab1');
          }
          
          if (wishlistTitle && !wishlistTitle.classList.contains('tab')) {
            wishlistTitle.classList.add('tab');
            wishlistTitle.setAttribute('data-tab', 'tab2');
            
            // Restore wishlist count
            if (this.wishlistCount) {
              wishlistTitle.textContent = wishlistTitle.textContent.replace(/\(\d+\)/, `(${this.wishlistCount})`);
            }
          }
          
          // Set active tab
          if (this.currentTab === 'tab1' && cartTitle) {
            cartTitle.classList.add('active');
            wishlistTitle?.classList.remove('active');
          } else if (this.currentTab === 'tab2' && wishlistTitle) {
            wishlistTitle.classList.add('active');
            cartTitle?.classList.remove('active');
          }
        }
      }
      
      sectionElement.innerHTML = newHTML;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      
      // Restore tab state after re-render
      setTimeout(() => {
        this.restoreTabState();
      }, 50);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
       {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
  
  // Custom method to initialize tabs
  initializeTabs() {
    const tabs = this.querySelectorAll('.tab');
    const contents = this.querySelectorAll('.content');
    
    if (tabs.length === 0) return;
    
    // Remove existing event listeners by cloning
    tabs.forEach(tab => {
      const newTab = tab.cloneNode(true);
      tab.parentNode.replaceChild(newTab, tab);
    });
    
    // Get fresh references
    const freshTabs = this.querySelectorAll('.tab');
    
    freshTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const target = tab.getAttribute('data-tab');
        this.switchTab(target);
      });
    });
    
    // Restore active tab
    if (this.currentTab) {
      this.switchTab(this.currentTab);
    }
  }
  
  // Custom method to switch tabs
  switchTab(targetTab) {
    const tabs = this.querySelectorAll('.tab');
    const contents = this.querySelectorAll('.content');
    
    // Update stored tab state
    this.currentTab = targetTab;
    
    // Update UI
    tabs.forEach(t => t.classList.remove('active'));
    contents.forEach(c => c.classList.remove('active'));
    
    const targetTabElement = this.querySelector(`.tab[data-tab="${targetTab}"]`);
    const targetContent = document.getElementById(targetTab);
    
    if (targetTabElement) targetTabElement.classList.add('active');
    if (targetContent) targetContent.classList.add('active');
  }
  
  // Custom method to restore tab state after re-render
  restoreTabState() {
    if (this.currentTab) {
      // Ensure tabs have proper classes
      const cartTitle = this.querySelector('.cart-title');
      const wishlistTitle = this.querySelector('.cart-wishlist');
      
      if (cartTitle && !cartTitle.classList.contains('tab')) {
        cartTitle.classList.add('tab');
        cartTitle.setAttribute('data-tab', 'tab1');
      }
      
      if (wishlistTitle && !wishlistTitle.classList.contains('tab')) {
        wishlistTitle.classList.add('tab');
        wishlistTitle.setAttribute('data-tab', 'tab2');
      }
      
      // Re-initialize tabs
      this.initializeTabs();
      
      // Switch to saved tab
      setTimeout(() => {
        this.switchTab(this.currentTab);
      }, 10);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);